/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from "react";
import { NavigableBreadcrumbLink } from "../../components/page_breadcrumbs/navigable_breadcrumb_link";
import { useParams, useLocation } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  FormControl,
  FormControlLabel,
  InputAdornment,
  Radio,
  RadioGroup,
} from "@mui/material";
import { MenuHeading } from "../../components/menu_heading";
import {
  StyledFieldContainer,
  StyledLeftFieldContainer,
  StyledTextField,
  StyledValue,
} from "../jobs/styles";
import { StyledNavLink } from "./styles";
import {
  StyledBreadCrumbContainer,
  StyledLink,
} from "../user_management/styles";
import api from "../../api";
import { useQuery } from "@tanstack/react-query";
import { Query_Keys } from "../../lib/queryKeys";
import { DEPARTMENT_LABELS } from "../../constants/filterOptions.js";

export const formatDefaultWage = (defaultWage) => {
  const numericWage = Number(defaultWage);

  if (!Number.isFinite(numericWage)) {
    return "0.00";
  }

  return numericWage.toFixed(2);
};

export const formatYesNo = (value) => {
  if (typeof value !== "boolean") {
    return "";
  }

  return value ? "Yes" : "No";
};

export const JobPost = () => {
  let { jobId } = useParams();
  const location = useLocation();

  const showJobDescription = async () => {
    const response = await api.get(`/v1/admin/jobs/${jobId}`)
    return response?.data
  }

  const { data } = useQuery({
    queryKey: Query_Keys.job_description({
      job_id: jobId,
    }),
    queryFn: showJobDescription,
    staleTime: 10 * 60 * 1000,
  })

  return (
    <React.Fragment>
      <div style={{height: "100%",
          width: "100%",
          overflowY: "scroll",
          overflowX: 'hidden',
          scrollbarWidth: "none",
      }}>
        <StyledBreadCrumbContainer>
          <Breadcrumbs>
            <NavigableBreadcrumbLink LinkComponent={StyledLink} label="Home" to="/admin/dashboard" />
            <StyledNavLink color={"#2583e3"} to="/admin/jobs" state={{ parentContext: location?.state?.parentContext }}>
              Jobs
            </StyledNavLink>
            <StyledLink color={"#515152"}>Job</StyledLink>
          </Breadcrumbs>
        </StyledBreadCrumbContainer>
        <MenuHeading report={false}>Job</MenuHeading>
        <Box sx={{}}>
          <StyledFieldContainer>
            <StyledLeftFieldContainer>Applies To</StyledLeftFieldContainer>
            <StyledValue>{data?.applies_to}</StyledValue>
          </StyledFieldContainer>

          <StyledFieldContainer>
            <StyledLeftFieldContainer>Job Title</StyledLeftFieldContainer>
            <StyledTextField value={data?.title} />
          </StyledFieldContainer>

          <StyledFieldContainer>
            <StyledLeftFieldContainer>Job Code</StyledLeftFieldContainer>
            <StyledTextField value={data?.code ? data?.code : ""} />
          </StyledFieldContainer>

          <StyledFieldContainer>
            <StyledLeftFieldContainer>GUID</StyledLeftFieldContainer>
            <StyledValue>{data?.guid}</StyledValue>
          </StyledFieldContainer>

          <StyledFieldContainer sx={{ alignItems: "flex-start" }}>
            <StyledLeftFieldContainer sx={{ paddingTop: "10px" }}>
              Pay Basis
            </StyledLeftFieldContainer>
            <div>
              <FormControl>
                <RadioGroup
                  key={data?.pay_basis}
                  defaultValue={data?.pay_basis}
                >
                  <FormControlLabel
                    value="HOURLY"
                    control={<Radio />}
                    label="hourly"
                  />
                  <FormControlLabel
                    value="SALARY"
                    control={<Radio />}
                    label="salary"
                  />
                </RadioGroup>
              </FormControl>
            </div>
          </StyledFieldContainer>

          <StyledFieldContainer>
            <StyledLeftFieldContainer>Department</StyledLeftFieldContainer>
            <StyledValue>{data ? (DEPARTMENT_LABELS[data?.department] || DEPARTMENT_LABELS.unmapped) : ""}</StyledValue>
          </StyledFieldContainer>

          <StyledFieldContainer>
            <StyledLeftFieldContainer>Default Pay</StyledLeftFieldContainer>
            <StyledTextField
              value={formatDefaultWage(data?.default_wage)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
              sx={{
                "& .MuiInputBase-root": {
                  paddingLeft: 0,
                },
              }}
            />
          </StyledFieldContainer>

          <StyledFieldContainer sx={{ alignItems: "flex-start" }}>
            <StyledLeftFieldContainer sx={{ paddingTop: "10px" }}>
              Tipped?
            </StyledLeftFieldContainer>
            <div>
              <FormControl>
                <RadioGroup key={data?.tipped} defaultValue={data?.tipped}>
                  <FormControlLabel
                    value={true}
                    control={<Radio />}
                    label="Yes. This employee must enter cash tips at closing."
                  />
                  <FormControlLabel
                    value={false}
                    control={<Radio />}
                    label="No tips need to be entered at closing."
                  />
                </RadioGroup>
              </FormControl>
            </div>
          </StyledFieldContainer>

          <StyledFieldContainer sx={{ alignItems: "flex-start" }}>
            <StyledLeftFieldContainer sx={{ paddingTop: "10px" }}>
              Cashier?
            </StyledLeftFieldContainer>
            <div>
              <FormControl>
                <RadioGroup key={data?.cashier} defaultValue={data?.cashier}>
                  <FormControlLabel
                    value={true}
                    control={<Radio />}
                    label="Yes"
                  />
                  <FormControlLabel
                    value={false}
                    control={<Radio />}
                    label="No"
                  />
                </RadioGroup>
              </FormControl>
            </div>
          </StyledFieldContainer>

          <StyledFieldContainer sx={{ alignItems: "flex-start" }}>
            <StyledLeftFieldContainer sx={{ paddingTop: "10px" }}>
              Driver?
            </StyledLeftFieldContainer>
            <div>
              <FormControl>
                <RadioGroup key={data?.driver} defaultValue={data?.driver}>
                  <FormControlLabel
                    value={true}
                    control={<Radio />}
                    label="Yes"
                  />
                  <FormControlLabel
                    value={false}
                    control={<Radio />}
                    label="No"
                  />
                </RadioGroup>
              </FormControl>
            </div>
          </StyledFieldContainer>

          <StyledFieldContainer>
            <StyledLeftFieldContainer>Exclude From Reporting?</StyledLeftFieldContainer>
            <StyledValue>{formatYesNo(data?.exclude_from_reporting)}</StyledValue>
          </StyledFieldContainer>
        </Box>
      </div>
    </React.Fragment>
  );
};
